import { AlertTriangle, Check, Copy } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useConfirm } from "@/hooks/use-confirm";
import { ApiError } from "@/lib/api/_client";
import { useCreateApiKey } from "@/lib/hooks/use-api-keys";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./api-key-dialog.i18n";

type ApiKeyDialogProps = {
  open: boolean;
  onClose: () => void;
};

export function ApiKeyDialog({ open, onClose }: ApiKeyDialogProps) {
  const t = useT(T);
  const [formData, setFormData] = useState({ name: "" });
  const [error, setError] = useState<string | null>(null);
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { confirm, ConfirmationDialog } = useConfirm();
  const createMutation = useCreateApiKey();

  useEffect(() => {
    if (!open) {
      setFormData({ name: "" });
      setError(null);
      setCreatedKey(null);
      setCopied(false);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError(t.nameRequired);
      return;
    }

    try {
      const result = await createMutation.mutateAsync(formData);
      setCreatedKey(result.plainTextKey);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(t.failedToCreateApiKey);
      }
    }
  };

  const handleCopy = async () => {
    if (createdKey) {
      await navigator.clipboard.writeText(createdKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = async () => {
    if (createdKey) {
      const confirmed = await confirm({
        title: t.copyApiKeyTitle,
        description: t.copyApiKeyDescription,
        confirmText: t.yesCopied,
        cancelText: t.notYet,
        variant: "default",
      });

      if (!confirmed) {
        return;
      }
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      {/* The key is created by the time it is on screen — from here the
          dialog is showing it to you, not holding unsaved input. */}
      <DialogContent className="sm:max-w-[600px]" guardDismiss={createdKey === null}>
        <DialogHeader>
          <DialogTitle>{createdKey ? t.apiKeyCreated : t.createApiKeyTitle}</DialogTitle>
          <DialogDescription>
            {createdKey ? t.apiKeyCreatedDescription : t.createApiKeyDescription}
          </DialogDescription>
        </DialogHeader>

        {!createdKey ? (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">
                  {t.name} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t.namePlaceholder}
                  required
                />
                <p className="text-sm text-muted-foreground">{t.nameHint}</p>
              </div>
            </div>

            <DialogFooter>
              <DialogCancel disabled={createMutation.isPending}>{t.cancel}</DialogCancel>
              <Button type="submit" loading={createMutation.isPending}>
                {t.createApiKey}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4 py-4">
            <div className="bg-warning/10 border border-warning/40 p-4 rounded-md">
              <div className="flex gap-2">
                <AlertTriangle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-semibold text-foreground">{t.saveApiKeyWarningTitle}</p>
                  <p className="text-sm text-muted-foreground">{t.saveApiKeyWarningDescription}</p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t.apiKeyLabel}</Label>
              <div className="flex gap-2">
                <Input value={createdKey} readOnly className="font-mono text-sm" />
                <Button
                  data-dialog-guard-ignore
                  type="button"
                  variant="outline"
                  size="icon"
                  tooltip={t.copy}
                  onClick={handleCopy}
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="bg-primary/10 border border-primary/30 p-4 rounded-md">
              <p className="text-sm text-primary">
                <strong>{t.howToUse}</strong> {t.howToUseDescription}
              </p>
              <code className="block mt-2 p-2 bg-card rounded text-xs">
                Authorization: Bearer {createdKey}
              </code>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>{t.done}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
      <ConfirmationDialog />
    </Dialog>
  );
}
